import React from 'react';
import { Clock } from 'lucide-react';
import { cn } from '../../utils/cn';

interface QuotaWidgetProps {
  used: number;
  limit: number;
  nextResetDate: string;
}

const QuotaWidget: React.FC<QuotaWidgetProps> = ({ used, limit, nextResetDate }) => {
  const percentage = Math.min((used / (limit || 1)) * 100, 100);
  
  const getBarColor = () => {
    if (percentage >= 90) return 'bg-error';
    if (percentage >= 75) return 'bg-amber-500';
    return 'bg-primary';
  };

  const getPercentageColor = () => {
    if (percentage >= 90) return 'text-error';
    if (percentage >= 75) return 'text-amber-600';
    return 'text-navy';
  };

  return (
    <div className="bg-surface-container-low rounded-2xl p-5 space-y-4">
      <div className="flex items-center justify-between">
        <div className="space-y-0.5">
          <span className="text-[10px] font-black uppercase tracking-widest text-secondary/60">
            Monthly Interactions
          </span>
          <div className="flex items-baseline gap-1">
            <span className={cn("text-lg font-display font-black", getPercentageColor())}>
              {used}
            </span>
            <span className="text-xs font-bold text-secondary/40">
              / {limit} used
            </span>
          </div>
        </div>
        <div className={cn(
          "w-10 h-10 rounded-xl flex items-center justify-center font-display font-black text-xs",
          percentage >= 90 ? "bg-error/10 text-error" : "bg-primary-fixed text-primary"
        )}>
          {Math.round(percentage)}%
        </div>
      </div>

      <div className="space-y-2">
        <div className="h-2 w-full bg-surface-container-highest rounded-full overflow-hidden">
          <div 
            className={cn("h-full rounded-full transition-all duration-700 ease-out", getBarColor())}
            style={{ width: `${percentage}%` }}
          />
        </div>
        
        <div className="flex items-center gap-2 text-[9px] font-bold text-secondary/60 uppercase tracking-wider">
          <Clock size={12} className="opacity-40" />
          <span>Resets on <span className="text-on-surface">{nextResetDate}</span></span>
        </div>
      </div>
      
      {percentage >= 90 && (
        <p className="text-[9px] font-black text-error uppercase leading-tight animate-pulse">
          Critical: Approaching monthly limit
        </p>
      )}
    </div>
  );
};

export default QuotaWidget;
